'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Users, Bell, BellOff } from 'lucide-react';
import type { DiscoverFeed } from './FeedDiscoveryContent';
import FeedTopicAvatar from '@/components/FeedTopicAvatar';
import RichBodyHtml from '@/components/RichBodyHtml';
import Badge from '@/components/ui/Badge';

interface FeedCardProps {
  feed: DiscoverFeed;
  onAction: (feed: DiscoverFeed) => Promise<void>;
}

/** Hauteur max « repliée » = évite line-clamp + ResizeObserver (oscillations → max update depth). */
const DESC_COLLAPSED_LINES = 3;

function sessionFollowLabel(n: number): string {
  if (n === 0) return 'Aucune session ne suit cette rubrique';
  if (n === 1) return '1 abonné suit cette rubrique';
  return `${n} abonnés suivent cette rubrique`;
}

export default function FeedCard({ feed, onAction }: FeedCardProps) {
  const [loading, setLoading] = useState(false);
  const descRef = useRef<HTMLDivElement | HTMLParagraphElement>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflows, setDescOverflows] = useState(false);
  const [descCollapsedMaxPx, setDescCollapsedMaxPx] = useState<number | null>(null);

  const richHtml = feed.descriptionHtml?.trim() ?? '';
  const plainDesc = feed.description?.trim() ?? '';
  const hasRichDesc = Boolean(richHtml);
  const hasAnyDesc = hasRichDesc || Boolean(plainDesc);

  useEffect(() => {
    setDescExpanded(false);
  }, [feed.id, feed.description, feed.descriptionHtml]);

  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el || !hasAnyDesc) {
      setDescOverflows(false);
      setDescCollapsedMaxPx(null);
      return;
    }
    const cs = getComputedStyle(el);
    let lineHeight = parseFloat(cs.lineHeight);
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      const fs = parseFloat(cs.fontSize) || 14;
      lineHeight = fs * 1.35;
    }
    const maxPx = Math.ceil(lineHeight * DESC_COLLAPSED_LINES);
    setDescCollapsedMaxPx(maxPx);
    if (descExpanded) return;
    setDescOverflows(el.scrollHeight > maxPx + 1);
  }, [feed.id, feed.description, feed.descriptionHtml, descExpanded, hasAnyDesc]);

  const handleClick = async () => {
    if (!feed.isGroupMember || loading) return;
    setLoading(true);
    try {
      await onAction(feed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="
        group relative bg-white rounded-3xl border border-[#E8E4FF] shadow-card
        transition-all duration-200 flex flex-col hover:scale-[1.02] hover:shadow-card-hover hover:border-[#DDD6FE]
      "
    >
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3">
          <FeedTopicAvatar
            feedIcon={feed.feedIcon}
            feedColor={feed.feedColor}
            groupIconUrl={feed.groupIconUrl}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-[#1E1B4B] leading-snug">{feed.title}</h3>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="info" size="sm" className="max-w-[120px] truncate">
                {feed.category}
              </Badge>
            </div>
          </div>
        </div>

        {hasAnyDesc ? (
          <div className="mt-3 flex-1">
            {hasRichDesc ? (
              <RichBodyHtml
                ref={descRef as React.RefObject<HTMLDivElement>}
                html={richHtml}
                className="topic-rich-body prose prose-sm max-w-none text-xs leading-relaxed text-[#333483] [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_a]:text-[#333483] [&_a]:font-bold [&_strong]:text-[#1E1B4B]/80"
                style={
                  !descExpanded && descOverflows && descCollapsedMaxPx != null
                    ? {
                        maxHeight: descCollapsedMaxPx,
                        overflow: 'hidden',
                      }
                    : undefined
                }
              />
            ) : (
              <p
                ref={descRef as React.RefObject<HTMLParagraphElement>}
                className="text-xs text-[#333483] leading-relaxed whitespace-pre-wrap"
                style={
                  !descExpanded && descOverflows && descCollapsedMaxPx != null
                    ? {
                        maxHeight: descCollapsedMaxPx,
                        overflow: 'hidden',
                      }
                    : undefined
                }
              >
                {plainDesc}
              </p>
            )}
            {descOverflows ? (
              <button
                type="button"
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-1.5 text-xs font-bold text-[#333483] hover:text-[#333483] hover:underline"
              >
                {descExpanded ? 'Voir moins' : 'Voir plus'}
              </button>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-[#333483] mt-3 italic">Aucune description</p>
        )}

        <div className="flex items-start gap-1.5 mt-4 pt-3 border-t border-[#F0EDFF] text-[#333483]">
          <Users size={12} className="flex-shrink-0 mt-0.5" />
          <span className="text-[11px] font-semibold leading-snug">
            {sessionFollowLabel(feed.followerCount)}
          </span>
        </div>
      </div>

      <div className="px-5 pb-5">
        {!feed.isGroupMember ? (
          <p className="text-center text-xs text-[#333483] leading-relaxed px-1">
            Rejoins la communauté pour t’abonner à cette rubrique.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => void handleClick()}
            disabled={loading}
            className={`
            w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-bold transition-all duration-200 active:scale-[0.97]
            ${feed.following
              ? 'bg-[#E4E3D8] text-[#333483] border border-[#E8E4FF] hover:bg-[#dcd8cc] hover:border-[#DDD6FE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#333483]/40 active:bg-[#FFF0F0] active:text-[#DC2626] active:border-[#FCA5A5]'
              : 'gradient-primary text-white shadow-md hover:shadow-lg hover:scale-[1.02]'
            }
            ${loading ? 'opacity-60 cursor-not-allowed' : ''}
          `}
          >
            {loading ? (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : feed.following ? (
              <>
                <BellOff size={14} />
                Abonné — Se désabonner
              </>
            ) : (
              <>
                <Bell size={14} />
                S’abonner
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
